import React, { useState } from "react"
import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Table,
  DialogActions,
  Button,
  LinearProgress
} from "@material-ui/core"
import { defineMessages } from "react-intl"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import useSmartFormat from "../../i18n/useSmartFormat"
import { words } from "../../i18n/lstrings"
import authSlice from "../../api/authSlice"
import { CancelToken } from "../../api/httpClient"
import { actions as notifier } from "../notifier"

const messages = defineMessages({
  manageMailPreferences: {
    id: "menu.manageMailPreferences",
    defaultMessage: "Manage mail preferences"
  },
  dialogWarning: {
    id: "mailPreferences.dialogWarning",
    defaultMessage:
      "Although certain mails from the web portal can be disabled, essential mails such as those concerning your account status, password recovery, and other emails concerning your account cannot be disabled."
  },
  subscription: {
    id: "mailPreferences.subscription",
    defaultMessage: "Subscription"
  },
  newReportsReceivedTitle: {
    id: "mailPreferences.newReportsReceived.title",
    defaultMessage: "New reports received"
  },
  newReportsReceivedDescription: {
    id: "mailPreferences.newReportsReceived.description",
    defaultMessage:
      "Receive an email notification when a new report is published"
  },
  newFormsReceivedTitle: {
    id: "mailPreferences.newFormsReceived.title",
    defaultMessage: "New forms received"
  },
  newFormsReceivedDescription: {
    id: "mailPreferences.newFormsReceived.description",
    defaultMessage: "Receive an email notification when a new form is available"
  },
  periodicFormRecallTitle: {
    id: "mailPreferences.periodicFormRecall.title",
    defaultMessage: "Periodic form recall"
  },
  periodicFormReceivedDescription: {
    id: "mailPreferences.periodicFormRecall.description",
    defaultMessage:
      "Receive an email notification when a periodic form requires your attention."
  },
  adminFormRelaunchTitle: {
    id: "mailPreferences.adminFormRelaunch.title",
    defaultMessage: "Form republication"
  },
  adminFormRelaunchDescription: {
    id: "mailPreferences.adminFormRelaunch.description",
    defaultMessage:
      "Receive a follow-up email notification when a form has been republished"
  },
  formSubmissionConfirmationTitle: {
    id: "mailPreferences.formSubmissionConfirmation.title",
    defaultMessage: "Form submission confirmation"
  },
  formSubmissionConfirmationDescription: {
    id: "mailPreferences.formSubmissionConfirmation.description",
    defaultMessage:
      "Receive an email notification when you have successfully submitted a form."
  },
  saveSuccess: {
    id: "mailPreferences.saveSuccess",
    defaultMessage: "Successfully saved email preferences"
  }
})

const NewReportsReceived = {
  key: "NewReportsReceived",
  title: messages.newReportsReceivedTitle,
  description: messages.newReportsReceivedDescription
}

const NewFormsReceived = {
  key: "NewFormsReceived",
  title: messages.newFormsReceivedTitle,
  description: messages.newFormsReceivedDescription
}

const PeriodicFormRecall = {
  key: "PeriodicFormRecall",
  title: messages.periodicFormRecallTitle,
  description: messages.periodicFormReceivedDescription
}

const AdminFormRelaunch = {
  key: "AdminFormRelaunch",
  title: messages.adminFormRelaunchTitle,
  description: messages.adminFormRelaunchDescription
}

const FormSubmission = {
  key: "FormSubmission",
  title: messages.formSubmissionConfirmationTitle,
  description: messages.formSubmissionConfirmationDescription
}

const MailSubscriptionScope = {
  NewReportsReceived,
  NewFormsReceived,
  PeriodicFormRecall,
  AdminFormRelaunch,
  FormSubmission
}

function MailScopeRow({ scope, isSubscribed, disabled, onSubscribeChange }) {
  const format = useSmartFormat()
  const { title, description } = scope

  function handleCheckboxChange(event) {
    onSubscribeChange(event.target.checked)
  }

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body1">{format(title)}</Typography>
        <Typography variant="body2">{format(description)}</Typography>
      </TableCell>
      <TableCell>
        <Checkbox
          color="secondary"
          checked={isSubscribed}
          onChange={handleCheckboxChange}
          disabled={disabled}
        />
      </TableCell>
    </TableRow>
  )
}

MailScopeRow.propTypes = {
  scope: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  isSubscribed: PropTypes.bool.isRequired,
  onSubscribeChange: PropTypes.func.isRequired
}

MailScopeRow.defaultProps = {
  disabled: false
}

export default function MailPreferencesDialog({ open, onClose }) {
  const dispatch = useDispatch()
  const format = useSmartFormat()
  const [isLoading, setIsLoading] = useState(false)
  const currentUnsubscriptions =
    useSelector((state) => state.auth.user.unsubscriptionScopes) ?? []

  const [unsubscriptionScopes, setUnsubscriptionScopes] = useState(
    currentUnsubscriptions
  )

  function handleClose() {
    if (isLoading) {
      return
    }

    // Reset on exit
    setUnsubscriptionScopes(currentUnsubscriptions)
    onClose()
  }

  function handleSave() {
    async function save() {
      setIsLoading(true)
      const { token } = CancelToken.source()
      try {
        await dispatch(
          authSlice.actions.updateUnsubscriptionScopes(
            unsubscriptionScopes,
            token
          )
        )
        onClose()
        await dispatch(notifier.enqueueSuccess(messages.saveSuccess))
      } finally {
        setIsLoading(false)
      }
    }

    save().then()
  }

  function createSubscriptionToggleHandler(key) {
    return (enabled) => {
      if (enabled) {
        setUnsubscriptionScopes(unsubscriptionScopes.filter((it) => it !== key))
      } else if (!unsubscriptionScopes.includes(key)) {
        setUnsubscriptionScopes([...unsubscriptionScopes, key])
      }
    }
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      {isLoading && <LinearProgress color="secondary" />}
      <DialogTitle>{format(messages.manageMailPreferences)}</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          {format(messages.dialogWarning)}
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{format(messages.subscription)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.values(MailSubscriptionScope).map((scope) => (
              <MailScopeRow
                key={scope.key}
                scope={scope}
                disabled={isLoading}
                isSubscribed={!unsubscriptionScopes.includes(scope.key)}
                onSubscribeChange={createSubscriptionToggleHandler(scope.key)}
              />
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={handleClose}>
          {format(words.cancel)}
        </Button>
        <Button disabled={isLoading} onClick={handleSave}>
          {format(words.save)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

MailPreferencesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
}
